import { useState } from "react";
import { Button, Input, Modal, Tag, message } from "antd";

export const MAX_RECIPIENTS = 10;
const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

// one address per entry: typed ones may be split by commas, semicolons or spaces
export const cleanEmails = (values) => {
  const out = [];
  values
    .flatMap((v) => String(v).split(/[\s,;]+/))
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean)
    .forEach((v) => {
      if (!out.includes(v)) out.push(v);
    });
  return out;
};

// "Email PDF": who to send the report's PDF to, and an optional note. The PDF is made here, then emailed.
const EmailPdfModal = ({ open, onClose, sendPdf }) => {
  const [typed, setTyped] = useState("");
  const [note, setNote] = useState("");
  const [step, setStep] = useState(null); // "making" | "sending" while it works
  const [error, setError] = useState("");

  const emails = cleanEmails([typed]);
  const wrong = emails.filter((e) => !EMAIL.test(e));
  const tooMany = emails.length > MAX_RECIPIENTS;
  const canSend = emails.length > 0 && !wrong.length && !tooMany && !step;

  const close = () => {
    if (step) return;
    setTyped("");
    setNote("");
    setError("");
    onClose();
  };

  const send = async () => {
    setError("");
    try {
      await sendPdf(emails, note.trim(), setStep);
      message.success(`PDF report sent to ${emails.length === 1 ? emails[0] : `${emails.length} people`}`);
      setStep(null);
      setTyped("");
      setNote("");
      onClose();
    } catch (e) {
      setStep(null);
      setError(e?.message || "The email could not be sent. Please try again.");
    }
  };

  return (
    <Modal
      open={open}
      title="Email PDF report"
      onCancel={close}
      maskClosable={!step}
      closable={!step}
      footer={[
        <Button key="cancel" onClick={close} disabled={!!step}>Cancel</Button>,
        <Button key="send" type="primary" onClick={send} disabled={!canSend} loading={!!step}>
          {step === "making" ? "Preparing PDF..." : step === "sending" ? "Sending..." : "Send"}
        </Button>,
      ]}
    >
      <p className="mb-1">Send to <span style={{ color: "#888", fontSize: "13px" }}>(separate addresses with commas)</span></p>
      <Input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onPressEnter={() => canSend && send()}
        placeholder="name@company.com, another@company.com"
        disabled={!!step}
        aria-label="Email addresses"
      />
      {emails.length > 0 && (
        <div className="mt-2">
          {emails.map((e) => (
            <Tag key={e} color={EMAIL.test(e) ? "blue" : "red"} className="mb-1">{e}</Tag>
          ))}
        </div>
      )}
      {wrong.length > 0 && <p className="text-danger mb-0 mt-1">Not an email address: {wrong.join(", ")}</p>}
      {tooMany && <p className="text-danger mb-0 mt-1">Up to {MAX_RECIPIENTS} email addresses at a time.</p>}

      <p className="mb-1 mt-3">Message (optional)</p>
      <Input.TextArea
        rows={3}
        maxLength={1000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note to go with the report"
        disabled={!!step}
        aria-label="Message"
      />
      <p className="mt-2 mb-0" style={{ color: "#888", fontSize: "13px" }}>
        The report is attached as a PDF. Replies come to your email address.
      </p>
      {error && <p className="text-danger mt-2 mb-0">{error}</p>}
    </Modal>
  );
};

export default EmailPdfModal;
